import type { InterviewKind } from '@Domain/Enums/Interviews/InterviewKind';
import type { LocalInterviewParticipant } from '@Domain/Ports/LocalInterviews/LocalInterviewPort';
import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import buildSubjectPath from '@UI/Pages/Beneficiaries/Shared/Helpers/buildSubjectPath';
import useAuthenticatedUser from '@UI/Providers/AuthenticatedUserProvider/useAuthenticatedUser';
import useStrictCurrentFacility from '@UI/Providers/CurrentFacilityProvider/useStrictCurrentFacility';
import useLocalInterviewPort from '@UI/Providers/PortsProvider/Hooks/LocalInterviews/useLocalInterviewPort';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';

export type NewLocalInterview = {
	readonly name: string;
	readonly kind: InterviewKind;
	/** `YYYY-MM-DD`. */
	readonly conductedOn: string;
	readonly participants: readonly LocalInterviewParticipant[];
	readonly preparationId: number;
	/** Epoch ms of the instant the participants were informed, read by the caller; unset when none applies. */
	readonly participantsInformedAt?: number;
};

type Result = {
	/** Writes the captured interview to the device, then opens its recording screen. The consent
	 * instant is read by the caller, never here: `null` means no consent applies. */
	readonly createLocalInterview: (interview: NewLocalInterview, consentGrantedAt: number | null) => void;
	readonly isSubmitting: boolean;
	readonly hasFailed: boolean;
};

/**
 * Creates the local interview for a capture and navigates to its recording. The preparation it belongs
 * to is written with it, so it travels to the server creation. No network call is made: the server
 * creation happens later, from the transfer handler.
 */
const useLocalInterviewCreation = (subject: SubjectAddress): Result => {
	const navigate = useNavigate();
	const { currentFacility } = useStrictCurrentFacility();
	const facilityId = currentFacility.id;
	const { user } = useAuthenticatedUser();
	const ownerUserId = user !== null ? user.id : null;
	const localInterviewPort = useLocalInterviewPort();

	const [isSubmitting, setIsSubmitting] = React.useState(false);
	const [hasFailed, setHasFailed] = React.useState(false);

	const createLocalInterview = React.useCallback(
		(interview: NewLocalInterview, consentGrantedAt: number | null) => {
			if (ownerUserId === null) {
				return;
			}

			setIsSubmitting(true);
			setHasFailed(false);

			const run = async (): Promise<void> => {
				try {
					const localInterview = await localInterviewPort.create({
						ownerUserId,
						facilityId,
						subject,
						name: interview.name,
						kind: interview.kind,
						conductedOn: interview.conductedOn,
						participants: interview.participants,
						consentGrantedAt,
						source: 'captured',
						preparationId: interview.preparationId,
						...(interview.participantsInformedAt !== undefined && {
							participantsInformedAt: interview.participantsInformedAt,
						}),
					});

					navigate(`${buildSubjectPath(subject)}/local-interviews/${localInterview.id}/recording`);
				} catch {
					setHasFailed(true);
				} finally {
					setIsSubmitting(false);
				}
			};

			void run();
		},
		[ownerUserId, facilityId, subject, localInterviewPort, navigate],
	);

	return { createLocalInterview, isSubmitting, hasFailed };
};

export default useLocalInterviewCreation;
